/**
 * Displays an overlay with a spinner
 * Parent container must have position=relative, if not using fullScreen mode
 */
import React from "react";
import cns from "classnames";
import { transparentize } from "polished";
import { makeStyles } from "@material-ui/core/styles";
import { Fade, Portal } from "@material-ui/core";
import { Spinner } from "./Spinner";
import { Subheading } from "./typography/Subheading";
import { ThemeType } from "@theme";

type Props = {
  /** title to show, if any */
  title?: string;
  /** renders spinner in a portal */
  fullScreen?: boolean;
  /**
   * Spinner size
   * @see https://material-ui.com/api/circular-progress/
   */
  size?: string | number;
  /** visible or not (default = true) */
  show: boolean;
  /** is bg transparent or grey? */
  transparent?: boolean;
  style?: React.CSSProperties;
};

export function SpinnerOverlay(props: Props) {
  const { fullScreen, title, show, size, transparent } = props;

  const classes = useStyles({});

  const rootClassName = cns(classes.root, {
    [classes.withBg]: !transparent
  });

  const overlay = (
    <Fade in={show} mountOnEnter unmountOnExit>
      <div className={rootClassName} style={props.style}>
        <div>
          <Spinner size={size} />
        </div>

        {title && (
          <div>
            <Subheading color="inherit" style={{ color: "white" }}>
              {title}
            </Subheading>
          </div>
        )}
      </div>
    </Fade>
  );

  return fullScreen ? <Portal>{overlay}</Portal> : overlay;
}

const useStyles = makeStyles((theme: ThemeType) => ({
  root: {
    ...theme.mixins.fullAbsolute,
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    zIndex: 3
  },

  withBg: {
    backgroundColor: transparentize(0.5, theme.palette.grey[400]),
    color: theme.palette.common.black
  }
}));
